import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';

// Define the WorkflowEntity interface
export interface WorkflowEntity {
  value: string;
  label: string;
  description: string;
  fields: string[];
  validation_rules: Record<string, {
    required?: boolean;
    max_length?: number;
    unique?: boolean;
    email?: boolean;
  }>;
  model?: string;
}

// Response is a direct array of entities, not paginated
export type WorkflowEntityResponse = WorkflowEntity[];

// Create API
export const workflowEntityApi = createApi({
  reducerPath: 'workflowEntityApi',
  baseQuery: createExtendedTokenBaseQuery('workflows/'),
  tagTypes: ['WorkflowEntity'],
  endpoints: (builder) => ({
    // Get all workflow entities
    getWorkflowEntities: builder.query<
      WorkflowEntityResponse,
      { dropdown?: boolean } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.dropdown) queryParams.append('dropdown', 'true');

        return {
          url: 'entities/',
          params: queryParams,
        };
      },
      providesTags: ['WorkflowEntity'],
    }),
    
    // Get workflow entity by value
    getWorkflowEntityByValue: builder.query<WorkflowEntity, string>({
      query: (value) => `entities/${value}/`,
      providesTags: (result, error, value) => [{ type: 'WorkflowEntity', id: value }],
    }),
    
    // Get workflow entity by ID
    getWorkflowEntityById: builder.query<WorkflowEntity, string>({
      query: (id) => `entities/${id}/`,
      providesTags: (result, error, id) => [{ type: 'WorkflowEntity', id }],
    }),
    
    // Check for duplicate workflows
    checkDuplicateWorkflow: builder.query<
      { isDuplicate: boolean; duplicateWorkflows: string[] },
      { entity_type: string; event_type?: string }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.entity_type) queryParams.append('entity_type', params.entity_type);
        if (params.event_type) queryParams.append('event_type', params.event_type);
        
        return { 
          url: 'workflow-templates/check-duplicate/', 
          params: queryParams 
        };
      },
    }),
  }),
});

// Export hooks
export const {
  useGetWorkflowEntitiesQuery,
  useGetWorkflowEntityByValueQuery,
  useGetWorkflowEntityByIdQuery,
  useCheckDuplicateWorkflowQuery,
} = workflowEntityApi;
